import { TypesOfContent } from '@app/graphql'
import { proxy } from 'valtio'

export interface DateRange {
  from: Date
  to: Date
  label: string
}

export interface FilterData {
  tipType: {
    id: number,
    label: string,
    value: string
  }
  analysis: {
    id: number,
    label: string,
    value: boolean
  }
  date: DateRange
  sports: string[]
}

interface FilterChannelData {
  filters: FilterData;
}

const createDateRange = (id: string): DateRange => {
  const today = new Date()


  const startOfToday = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
    0, 0, 0, 0
  ))

  const endOfToday = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
    23, 59, 59, 999
  ))

  switch (id) {
    case 'all':
      return {
        from: null,
        to: null,
        label: 'All'
      }
    case 'today':
      return {
        from: startOfToday,
        to: endOfToday,
        label: 'Today'
      }
    case 'yesterday': {
      const yesterday = new Date(today)
      yesterday.setUTCDate(today.getUTCDate() - 1)
      const startOfYesterday = new Date(Date.UTC(
        yesterday.getUTCFullYear(),
        yesterday.getUTCMonth(),
        yesterday.getUTCDate(),
        0, 0, 0, 0
      ))
      const endOfYesterday = new Date(Date.UTC(
        yesterday.getUTCFullYear(),
        yesterday.getUTCMonth(),
        yesterday.getUTCDate(),
        23, 59, 59, 999
      ))
      return {
        from: startOfYesterday,
        to: endOfYesterday,
        label: 'Yesterday'
      }
    }
    case 'minus2': {
      const twoDaysAgo = new Date(today)
      twoDaysAgo.setUTCDate(today.getUTCDate() - 2)
      const startOfTwoDaysAgo = new Date(Date.UTC(
        twoDaysAgo.getUTCFullYear(),
        twoDaysAgo.getUTCMonth(),
        twoDaysAgo.getUTCDate(),
        0, 0, 0, 0
      ))
      const endOfTwoDaysAgo = new Date(Date.UTC(
        twoDaysAgo.getUTCFullYear(),
        twoDaysAgo.getUTCMonth(),
        twoDaysAgo.getUTCDate(),
        23, 59, 59, 999
      ))
      return {
        from: startOfTwoDaysAgo,
        to: endOfTwoDaysAgo,
        label: '2 days ago'
      }
    }
    case 'minus3': {
      const threeDaysAgo = new Date(today)
      threeDaysAgo.setUTCDate(today.getUTCDate() - 3)
      const startOfThreeDaysAgo = new Date(Date.UTC(
        threeDaysAgo.getUTCFullYear(),
        threeDaysAgo.getUTCMonth(),
        threeDaysAgo.getUTCDate(),
        0, 0, 0, 0
      ))
      const endOfThreeDaysAgo = new Date(Date.UTC(
        threeDaysAgo.getUTCFullYear(),
        threeDaysAgo.getUTCMonth(),
        threeDaysAgo.getUTCDate(),
        23, 59, 59, 999
      ))
      return {
        from: startOfThreeDaysAgo,
        to: endOfThreeDaysAgo,
        label: '3 days ago'
      }
    }
    default:
      return {
        from: startOfToday,
        to: endOfToday,
        label: 'Today'
      }
  }
}

const defaultFilters: FilterData = {
  tipType: { id: 1, label: 'All', value: null },
  analysis: { id: 1, label: 'All', value: null },
  date: createDateRange('all'),
  sports: []
}

const initialState: FilterChannelData = {
  filters: defaultFilters
}

export const FilterChannelState = proxy<FilterChannelData>(initialState)

export const updateChannelFilters = (filters: Partial<FilterData>) => {
  FilterChannelState.filters = {
    ...FilterChannelState.filters,
    ...filters
  }
}

export const resetChannelFilters = () => {
  FilterChannelState.filters = { ...defaultFilters }
}

export const getChannelFilters = (): FilterData => {
  return FilterChannelState.filters
}

export const hasActiveFilters = (): boolean => {
  const current = FilterChannelState.filters
  return (
    current.tipType !== defaultFilters.tipType ||
    current.analysis !== defaultFilters.analysis ||
    current.date.label !== defaultFilters.date.label ||
    current.sports.length !== defaultFilters.sports.length ||
    !current.sports.every(sport => defaultFilters.sports.includes(sport))
  )
}

export const getGraphQLFilterVariables = () => {
  const filters = FilterChannelState.filters
  let contentTypes = null
  let createdAfter = null
  let createdBefore = null

  if (FilterChannelState.filters.tipType.value === 'Multi') {
    contentTypes = [ TypesOfContent.Multitip ]
  } else if (FilterChannelState.filters.tipType.value === 'Single') {
    contentTypes = [ TypesOfContent.Tip ]
  } else if (filters.sports.length > 0) {
    contentTypes = [ TypesOfContent.Tip, TypesOfContent.Multitip ]
  }

  if(filters.date){
    createdAfter = filters.date.from
    createdBefore = filters.date.to
  }

  return {
    ...(contentTypes && { contentTypes }),
    analysis: filters.analysis.value,
    sportIds: filters.sports.map(Number),
    ...(createdAfter && { createdAfter }),
    ...(createdBefore && { createdBefore }),
  }
}

export const resetFiltersToToday = () => {
  FilterChannelState.filters = {
    ...FilterChannelState.filters,
    date: createDateRange('Today')
  }
}

export const getActiveFilterCount = (): number => {
  let count = 0
  const current = FilterChannelState.filters
  const defaults = defaultFilters

  if (current.tipType.id !== defaults.tipType.id) {
    count++
  }

  if (current.analysis.id !== defaults.analysis.id) {
    count++
  }

  if (current.date.label !== defaults.date.label) {
    count++
  }

  if (current.sports.length > 0) {
    count++
  } else {
    count--

  }

  return count
}

export { createDateRange }